import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import QRPoster from "@/components/QRPoster";

const blankDest = { title: "", location: "", country: "", description: "", image_url: "", start_date: "", end_date: "", status: "upcoming", lat: null, lng: null };
const blankRes = { title: "", description: "", category: "general", url: "", body: "" };
const blankPod = { title: "", description: "", embed_url: "", episode_date: "", guest: "" };
const blankEvent = { title: "", description: "", image_url: "", recurring: true, event_date: "", location: "", cta_label: "Learn more", cta_url: "" };
const blankPost = { title: "", body: "", excerpt: "", image_url: "" };
const blankQuote = { text: "", author: "" };

export default function Admin() {
  return (
    <div data-testid="admin-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <div className="overline mb-3">Admin</div>
      <h1 className="font-serif-display text-4xl md:text-5xl mb-10">Dashboard</h1>

      <Tabs defaultValue="destinations" className="w-full">
        <TabsList className="bg-[var(--bg-alt)] mb-8 flex-wrap h-auto">
          <TabsTrigger data-testid="admin-tab-destinations" value="destinations">Destinations</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-events" value="events">Events</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-podcast" value="podcast">Podcast</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-journal" value="journal">Journal</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-quotes" value="quotes">Quotes</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-resources" value="resources">Resources</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-pledges" value="pledges">Wall pledges</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-about" value="about">Our story</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-press" value="press">Press kit</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-notifications" value="notifications">Notifications</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-donation" value="donation">Donation</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-social" value="social">Social</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-poster" value="poster">QR Poster</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-album" value="album">Album</TabsTrigger>
        </TabsList>

        <TabsContent value="destinations"><DestinationsAdmin /></TabsContent>
        <TabsContent value="events"><EventsAdmin /></TabsContent>
        <TabsContent value="podcast"><PodcastAdmin /></TabsContent>
        <TabsContent value="journal"><JournalAdmin /></TabsContent>
        <TabsContent value="quotes"><QuotesAdmin /></TabsContent>
        <TabsContent value="resources"><ResourcesAdmin /></TabsContent>
        <TabsContent value="pledges"><PledgesAdmin /></TabsContent>
        <TabsContent value="about"><AboutAdmin /></TabsContent>
        <TabsContent value="press"><PressAdmin /></TabsContent>
        <TabsContent value="notifications"><NotificationsAdmin /></TabsContent>
        <TabsContent value="donation"><DonationAdmin /></TabsContent>
        <TabsContent value="social"><SocialAdmin /></TabsContent>
        <TabsContent value="poster"><QRPoster /></TabsContent>
        <TabsContent value="album"><AlbumAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Reusable inputs ---------- */

function Field({ label, ...props }) {
  return (
    <label className="block">
      <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">{label}</div>
      <input {...props} className="w-full px-3 py-2.5 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]" />
    </label>
  );
}

function TextArea({ label, ...props }) {
  return (
    <label className="block">
      <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">{label}</div>
      <textarea {...props} rows={props.rows || 3} className="w-full px-3 py-2.5 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]" />
    </label>
  );
}

function ItemRow({ children, onEdit, onDelete, editTestId, deleteTestId }) {
  return (
    <div className="p-4 border border-[var(--line)] rounded-sm flex justify-between items-start gap-3">
      <div className="flex-1 min-w-0">{children}</div>
      <div className="flex items-center gap-1 shrink-0">
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            data-testid={editTestId}
            className="p-1.5 text-[var(--ink-soft)] hover:text-[var(--forest)] hover:bg-[var(--bg-alt)] rounded-sm"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            data-testid={deleteTestId}
            className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-sm"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function EditModal({ open, onOpenChange, title, onSave, saving, children }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">{children}</div>
        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm border border-[var(--line)] rounded-sm hover:bg-[var(--bg-alt)]"
            data-testid="edit-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-[var(--terracotta)] text-[var(--ink)] rounded-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50"
            data-testid="edit-modal-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Destinations ---------- */

function DestinationsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankDest);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  const load = async () => setItems((await api.get("/destinations")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (uploading) { toast.error("Please wait for the image upload to finish."); return; }
    try { await api.post("/destinations", form); setForm(blankDest); toast.success("Destination added"); load(); }
    catch { toast.error("Failed to add"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this destination?")) return;
    await api.delete(`/destinations/${id}`); toast.success("Deleted"); load();
  };
  const save = async () => {
    if (editUploading) { toast.error("Please wait for the image upload to finish."); return; }
    setSaving(true);
    try {
      await api.put(`/destinations/${editing.id}`, {
        title: editing.title, location: editing.location, country: editing.country || "",
        description: editing.description || "", image_url: editing.image_url || "",
        lat: editing.lat, lng: editing.lng,
        start_date: editing.start_date || null, end_date: editing.end_date || null,
        status: editing.status,
      });
      toast.success("Updated"); setEditing(null); load();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };

  const set = (k) => (e) => setEditing((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <form onSubmit={submit} className="space-y-3 p-6 border border-[var(--line)] rounded-sm" data-testid="dest-form">
        <h3 className="font-serif-display text-2xl mb-4">New destination</h3>
        <Field data-testid="dest-title" label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Field data-testid="dest-location" label="Location (city)" required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        <Field data-testid="dest-country" label="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
        <TextArea data-testid="dest-description" label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <ImageUploader label="Image" testid="dest-image" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} onUploadingChange={setUploading} />
        <div className="grid grid-cols-2 gap-3">
          <Field data-testid="dest-lat" label="Latitude (auto if blank)" type="number" step="any" value={form.lat ?? ""} onChange={(e) => setForm({ ...form, lat: e.target.value === "" ? null : parseFloat(e.target.value) })} />
          <Field data-testid="dest-lng" label="Longitude (auto if blank)" type="number" step="any" value={form.lng ?? ""} onChange={(e) => setForm({ ...form, lng: e.target.value === "" ? null : parseFloat(e.target.value) })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field data-testid="dest-start" label="Start date" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          <Field data-testid="dest-end" label="End date" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
        <label className="block">
          <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">Status</div>
          <select data-testid="dest-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full px-3 py-2.5 border border-[var(--line)] rounded-sm text-sm bg-[var(--bg)]">
            <option value="past">past</option>
            <option value="current">current</option>
            <option value="upcoming">upcoming</option>
          </select>
        </label>
        <button data-testid="dest-submit" className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)]">
          Add destination
        </button>
      </form>

      <div className="space-y-3">
        <h3 className="font-serif-display text-2xl mb-4">Existing ({items.length})</h3>
        {items.map((d) => (
          <ItemRow key={d.id}
            onEdit={() => setEditing({ ...d, lat: d.lat ?? null, lng: d.lng ?? null })}
            onDelete={() => del(d.id)}
            editTestId={`dest-edit-${d.id}`}
            deleteTestId={`dest-delete-${d.id}`}
          >
            <div className="font-medium">{d.location} <span className="text-xs text-[var(--ink-soft)]">· {d.status}</span></div>
            <div className="text-xs text-[var(--ink-soft)]">{d.title}</div>
          </ItemRow>
        ))}
      </div>

      {editing && (
        <EditModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Edit destination" onSave={save} saving={saving || editUploading}>
          <Field data-testid="dest-edit-title" label="Title" value={editing.title || ""} onChange={set("title")} />
          <Field data-testid="dest-edit-location" label="Location" value={editing.location || ""} onChange={set("location")} />
          <Field data-testid="dest-edit-country" label="Country" value={editing.country || ""} onChange={set("country")} />
          <TextArea data-testid="dest-edit-description" label="Description" value={editing.description || ""} onChange={set("description")} />
          <ImageUploader label="Image" testid="dest-edit-image" value={editing.image_url || ""} onChange={(url) => setEditing((prev) => ({ ...prev, image_url: url }))} onUploadingChange={setEditUploading} />
          <div className="grid grid-cols-2 gap-3">
            <Field data-testid="dest-edit-lat" label="Latitude" type="number" step="any" value={editing.lat ?? ""} onChange={(e) => setEditing((prev) => ({ ...prev, lat: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
            <Field data-testid="dest-edit-lng" label="Longitude" type="number" step="any" value={editing.lng ?? ""} onChange={(e) => setEditing((prev) => ({ ...prev, lng: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field data-testid="dest-edit-start" label="Start date" type="date" value={editing.start_date || ""} onChange={set("start_date")} />
            <Field data-testid="dest-edit-end" label="End date" type="date" value={editing.end_date || ""} onChange={set("end_date")} />
          </div>
          <label className="block">
            <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">Status</div>
            <select data-testid="dest-edit-status" value={editing.status || "upcoming"} onChange={set("status")}
              className="w-full px-3 py-2.5 border border-[var(--line)] rounded-sm text-sm bg-[var(--bg)]">
              <option value="past">past</option>
              <option value="current">current</option>
              <option value="upcoming">upcoming</option>
            </select>
          </label>
        </EditModal>
      )}
    </div>
  );
}

/* ---------- Events ---------- */

function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankEvent);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  const load = async () => setItems((await api.get("/events")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (uploading) { toast.error("Please wait for the image upload to finish."); return; }
    try { await api.post("/events", form); setForm(blankEvent); toast.success("Event added"); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`); load();
  };
  const save = async () => {
    if (editUploading) { toast.error("Please wait for the image upload to finish."); return; }
    setSaving(true);
    try {
      await api.put(`/events/${editing.id}`, {
        title: editing.title, description: editing.description || "",
        image_url: editing.image_url || "", recurring: !!editing.recurring,
        event_date: editing.event_date || null, location: editing.location || "",
        cta_label: editing.cta_label || "Learn more", cta_url: editing.cta_url || "",
      });
      toast.success("Updated"); setEditing(null); load();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    // Optimistic reorder + persist
    const next = [...items];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    setItems(next);
    try {
      await api.post("/events/reorder", { ordered_ids: next.map((e) => e.id) });
    } catch {
      toast.error("Reorder failed");
      load();
    }
  };

  const set = (k) => (e) => setEditing((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <form onSubmit={submit} className="space-y-3 p-6 border border-[var(--line)] rounded-sm">
        <h3 className="font-serif-display text-2xl mb-4">New event</h3>
        <Field data-testid="event-title" label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <TextArea data-testid="event-desc" label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <ImageUploader label="Event photo" testid="event-image" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} onUploadingChange={setUploading} />
        <div className="grid grid-cols-2 gap-3">
          <Field data-testid="event-location" label="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <Field data-testid="event-date" label="Date (optional)" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="event-recurring" type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />
          Recurring (no specific date)
        </label>
        <Field data-testid="event-cta-label" label="CTA label" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} />
        <Field data-testid="event-cta-url" label="CTA URL (signup / RSVP link)" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} />
        <button data-testid="event-submit" disabled={uploading} className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50">
          {uploading ? "Uploading image…" : "Add event"}
        </button>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif-display text-2xl mb-1">Existing ({items.length})</h3>
        <p className="text-xs text-[var(--ink-soft)] mb-4">Use ↑ / ↓ to rearrange — the order here is the order visitors see.</p>
        {items.map((e, i) => (
          <div key={e.id} className="flex items-stretch gap-2">
            <div className="flex flex-col justify-center gap-1 shrink-0">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                className="px-2 py-1 border border-[var(--line)] rounded text-xs disabled:opacity-30 hover:bg-[var(--bg-alt)]"
                data-testid={`event-up-${e.id}`} aria-label="Move up">↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1}
                className="px-2 py-1 border border-[var(--line)] rounded text-xs disabled:opacity-30 hover:bg-[var(--bg-alt)]"
                data-testid={`event-down-${e.id}`} aria-label="Move down">↓</button>
            </div>
            <div className="flex-1">
              <ItemRow
                onEdit={() => setEditing({ ...e })}
                onDelete={() => del(e.id)}
                editTestId={`event-edit-${e.id}`}
                deleteTestId={`event-delete-${e.id}`}
              >
                <div className="flex items-center gap-3">
                  {e.image_url && (
                    <img
                      src={e.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${e.image_url}` : e.image_url}
                      alt=""
                      className="w-14 h-14 object-cover rounded-sm shrink-0"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium truncate">{e.title}</div>
                    <div className="text-xs text-[var(--ink-soft)] truncate">{e.recurring ? "Recurring" : e.event_date} {e.location && `· ${e.location}`}</div>
                  </div>
                </div>
              </ItemRow>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Edit event" onSave={save} saving={saving || editUploading}>
          <Field data-testid="event-edit-title" label="Title" value={editing.title || ""} onChange={set("title")} />
          <TextArea data-testid="event-edit-desc" label="Description" value={editing.description || ""} onChange={set("description")} rows={4} />
          <ImageUploader label="Event photo" testid="event-edit-image" value={editing.image_url || ""} onChange={(url) => setEditing((prev) => ({ ...prev, image_url: url }))} onUploadingChange={setEditUploading} />
          <div className="grid grid-cols-2 gap-3">
            <Field data-testid="event-edit-location" label="Location" value={editing.location || ""} onChange={set("location")} />
            <Field data-testid="event-edit-date" label="Date" type="date" value={editing.event_date || ""} onChange={set("event_date")} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="event-edit-recurring" type="checkbox" checked={!!editing.recurring} onChange={(e) => setEditing((prev) => ({ ...prev, recurring: e.target.checked }))} />
            Recurring (no specific date)
          </label>
          <Field data-testid="event-edit-cta-label" label="CTA label" value={editing.cta_label || ""} onChange={set("cta_label")} />
          <Field data-testid="event-edit-cta-url" label="CTA URL" value={editing.cta_url || ""} onChange={set("cta_url")} />
        </EditModal>
      )}
    </div>
  );
}

/* ---------- Podcast ---------- */

function PodcastAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankPod);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => setItems((await api.get("/podcast")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/podcast", form); setForm(blankPod); toast.success("Episode added"); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this episode?")) return;
    await api.delete(`/podcast/${id}`); load();
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/podcast/${editing.id}`, {
        title: editing.title, description: editing.description || "",
        embed_url: editing.embed_url || "", episode_date: editing.episode_date || null,
        guest: editing.guest || "",
      });
      toast.success("Updated"); setEditing(null); load();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };
  const set = (k) => (e) => setEditing((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <form onSubmit={submit} className="space-y-3 p-6 border border-[var(--line)] rounded-sm">
        <h3 className="font-serif-display text-2xl mb-4">New episode</h3>
        <Field data-testid="pod-title" label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Field data-testid="pod-guest" label="Guest" value={form.guest} onChange={(e) => setForm({ ...form, guest: e.target.value })} />
        <TextArea data-testid="pod-desc" label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Field data-testid="pod-embed" label="Embed URL (Spotify or YouTube)" value={form.embed_url} onChange={(e) => setForm({ ...form, embed_url: e.target.value })} />
        <Field data-testid="pod-date" label="Episode date" type="date" value={form.episode_date} onChange={(e) => setForm({ ...form, episode_date: e.target.value })} />
        <button data-testid="pod-submit" className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)]">Add episode</button>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif-display text-2xl mb-4">Existing ({items.length})</h3>
        {items.map((p) => (
          <ItemRow key={p.id}
            onEdit={() => setEditing({ ...p })}
            onDelete={() => del(p.id)}
            editTestId={`pod-edit-${p.id}`}
            deleteTestId={`pod-delete-${p.id}`}
          >
            <div className="font-medium truncate">{p.title}</div>
            <div className="text-xs text-[var(--ink-soft)] truncate">{p.episode_date} {p.guest && `· ${p.guest}`}</div>
          </ItemRow>
        ))}
      </div>

      {editing && (
        <EditModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Edit episode" onSave={save} saving={saving}>
          <Field data-testid="pod-edit-title" label="Title" value={editing.title || ""} onChange={set("title")} />
          <Field data-testid="pod-edit-guest" label="Guest" value={editing.guest || ""} onChange={set("guest")} />
          <TextArea data-testid="pod-edit-desc" label="Description" value={editing.description || ""} onChange={set("description")} rows={4} />
          <Field data-testid="pod-edit-embed" label="Embed URL" value={editing.embed_url || ""} onChange={set("embed_url")} />
          <Field data-testid="pod-edit-date" label="Episode date" type="date" value={editing.episode_date || ""} onChange={set("episode_date")} />
        </EditModal>
      )}
    </div>
  );
}

/* ---------- Resources ---------- */

function ResourcesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankRes);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => setItems((await api.get("/resources")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/resources", form); setForm(blankRes); toast.success("Added"); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    await api.delete(`/resources/${id}`); load();
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/resources/${editing.id}`, {
        title: editing.title, description: editing.description || "",
        category: editing.category || "general", url: editing.url || "",
        body: editing.body || "",
      });
      toast.success("Updated"); setEditing(null); load();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };
  const set = (k) => (e) => setEditing((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <form onSubmit={submit} className="space-y-3 p-6 border border-[var(--line)] rounded-sm">
        <h3 className="font-serif-display text-2xl mb-4">New resource</h3>
        <Field data-testid="res-title" label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Field data-testid="res-category" label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        <TextArea data-testid="res-desc" label="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <TextArea data-testid="res-body" label="Body (optional)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        <Field data-testid="res-url" label="External link" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
        <button data-testid="res-submit" className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)]">Add resource</button>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif-display text-2xl mb-4">Existing ({items.length})</h3>
        {items.map((r) => (
          <ItemRow key={r.id}
            onEdit={() => setEditing({ ...r })}
            onDelete={() => del(r.id)}
            editTestId={`res-edit-${r.id}`}
            deleteTestId={`res-delete-${r.id}`}
          >
            <div className="font-medium truncate">{r.title}</div>
            <div className="text-xs text-[var(--ink-soft)] truncate">{r.category}</div>
          </ItemRow>
        ))}
      </div>

      {editing && (
        <EditModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Edit resource" onSave={save} saving={saving}>
          <Field data-testid="res-edit-title" label="Title" value={editing.title || ""} onChange={set("title")} />
          <Field data-testid="res-edit-category" label="Category" value={editing.category || ""} onChange={set("category")} />
          <TextArea data-testid="res-edit-desc" label="Short description" value={editing.description || ""} onChange={set("description")} />
          <TextArea data-testid="res-edit-body" label="Body" value={editing.body || ""} onChange={set("body")} rows={5} />
          <Field data-testid="res-edit-url" label="External link" value={editing.url || ""} onChange={set("url")} />
        </EditModal>
      )}
    </div>
  );
}

/* ---------- Pledges ---------- */

function PledgesAdmin() {
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/sobriety/pledges")).data);
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this pledge?")) return;
    await api.delete(`/sobriety/pledges/${id}`); toast.success("Deleted"); load();
  };
  return (
    <div className="space-y-3">
      <h3 className="font-serif-display text-2xl mb-4">Wall pledges ({items.length})</h3>
      {items.length === 0 && <p className="text-sm text-[var(--ink-soft)]">No pledges yet.</p>}
      {items.map((p) => (
        <div key={p.id} className="p-4 border border-[var(--line)] rounded-sm flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              {p.name}
              {p.state && <span className="text-xs text-[var(--ink-soft)] ml-2">· {p.state}</span>}
              <span className="text-xs text-[var(--forest)] ml-2 font-mono-acc">{p.days} days</span>
            </div>
            <div className="text-xs text-[var(--ink-soft)] mt-0.5">Sober since {p.sobriety_date}</div>
            {p.note && <div className="text-sm text-[var(--ink-soft)] mt-2 italic">&ldquo;{p.note}&rdquo;</div>}
          </div>
          <button data-testid={`pledge-delete-${p.id}`} onClick={() => del(p.id)} className="text-red-600 hover:text-red-800">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

/* ---------- Nominations ---------- */

function NominationsAdmin() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/nominations?status=${status}`);
    setItems(data);
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => {
    if (!window.confirm("Approve this nomination? It will appear on the Wall of Hope.")) return;
    try {
      await api.post(`/admin/nominations/${id}/approve`);
      toast.success("Approved — hero is now on the Wall of Hope");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };
  const reject = async (id) => {
    if (!window.confirm("Reject this nomination?")) return;
    try {
      await api.post(`/admin/nominations/${id}/reject`);
      toast.success("Rejected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Permanently delete this record?")) return;
    await api.delete(`/admin/nominations/${id}`);
    toast.success("Deleted");
    load();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h3 className="font-serif-display text-2xl">Hero nominations</h3>
        <div className="inline-flex rounded-sm border border-[var(--line)] overflow-hidden text-xs">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button key={s} data-testid={`nom-status-${s}`} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 capitalize transition ${status === s ? "bg-[var(--ink)] text-[var(--bg)]" : "hover:bg-[var(--bg-alt)]"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      {items.length === 0 && (
        <p className="text-sm text-[var(--ink-soft)] py-8 text-center border border-dashed border-[var(--line)] rounded-sm">
          No {status === "all" ? "" : status} nominations.
        </p>
      )}
      {items.map((n) => (
        <div key={n.id} data-testid={`nom-row-${n.id}`} className="p-4 border border-[var(--line)] rounded-sm flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium">{n.hero_name}</span>
              {n.state && <span className="text-xs text-[var(--ink-soft)]">· {n.state}</span>}
              <span className="text-xs text-[var(--forest)] font-mono-acc">Sober since {n.sobriety_date}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono-acc uppercase tracking-wider ${
                n.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                n.status === "approved" ? "bg-green-100 text-green-800" :
                "bg-red-100 text-red-800"
              }`}>{n.status}</span>
            </div>
            <div className="text-xs text-[var(--ink-soft)] mt-1">
              Nominated by {n.nominator_name} &lt;{n.nominator_email}&gt;
            </div>
            {n.note && <div className="text-sm text-[var(--ink-soft)] mt-2 italic">&ldquo;{n.note}&rdquo;</div>}
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            {n.status === "pending" && (
              <>
                <button data-testid={`nom-approve-${n.id}`} onClick={() => approve(n.id)}
                  className="text-xs px-3 py-1.5 bg-[var(--forest)] text-white rounded-sm hover:bg-[var(--terracotta)]">
                  Approve
                </button>
                <button data-testid={`nom-reject-${n.id}`} onClick={() => reject(n.id)}
                  className="text-xs px-3 py-1.5 border border-[var(--line)] rounded-sm hover:border-red-600 hover:text-red-600">
                  Reject
                </button>
              </>
            )}
            <button data-testid={`nom-delete-${n.id}`} onClick={() => remove(n.id)} className="text-red-600 hover:text-red-800 self-end mt-1">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- About ---------- */

function AboutAdmin() {
  const blank = { couple_photo: "", intro: "", his_name: "", his_story: "", her_name: "", her_story: "", our_story: "", closing: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get("/about").then((r) => setForm({ ...blank, ...r.data })); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (uploading) { toast.error("Please wait for the image upload to finish."); return; }
    setSaving(true);
    try { await api.put("/about", form); toast.success("Our story saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="max-w-3xl space-y-4 p-6 border border-[var(--line)] rounded-sm" data-testid="about-form">
      <h3 className="font-serif-display text-2xl mb-2">Our story</h3>
      <p className="text-xs text-[var(--ink-soft)] mb-4">Every field below renders on the public /about page. Line breaks become paragraph breaks.</p>
      <ImageUploader label="Couple photo" testid="about-photo" value={form.couple_photo} onChange={(url) => setForm({ ...form, couple_photo: url })} onUploadingChange={setUploading} />
      <TextArea data-testid="about-intro" label="Intro (1-2 sentences)" value={form.intro} onChange={set("intro")} />
      <div className="grid grid-cols-2 gap-3">
        <Field data-testid="about-his-name" label="His name" value={form.his_name} onChange={set("his_name")} />
        <Field data-testid="about-her-name" label="Her name" value={form.her_name} onChange={set("her_name")} />
      </div>
      <TextArea data-testid="about-his-story" label="His chapter" value={form.his_story} onChange={set("his_story")} rows={5} />
      <TextArea data-testid="about-her-story" label="Her chapter" value={form.her_story} onChange={set("her_story")} rows={5} />
      <TextArea data-testid="about-our-story" label="Our chapter" value={form.our_story} onChange={set("our_story")} rows={5} />
      <TextArea data-testid="about-closing" label="Closing line" value={form.closing} onChange={set("closing")} />
      <button data-testid="about-submit" disabled={saving || uploading} className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50">
        {uploading ? "Uploading image…" : saving ? "Saving..." : "Save"}
      </button>
    </form>
  );
}

/* ---------- Press ---------- */

function PressAdmin() {
  const blank = { mission: "", photo1: "", photo2: "", contact_email: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploading1, setUploading1] = useState(false);
  const [uploading2, setUploading2] = useState(false);
  useEffect(() => { api.get("/press").then((r) => setForm({ ...blank, ...r.data })); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (uploading1 || uploading2) { toast.error("Please wait for the image upload to finish."); return; }
    setSaving(true);
    try { await api.put("/press", form); toast.success("Press kit saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="max-w-3xl space-y-4 p-6 border border-[var(--line)] rounded-sm">
      <h3 className="font-serif-display text-2xl mb-2">Press kit</h3>
      <TextArea data-testid="press-mission" label="Mission paragraph" value={form.mission} onChange={set("mission")} rows={5} />
      <ImageUploader label="Photo 1" testid="press-photo1" value={form.photo1} onChange={(url) => setForm({ ...form, photo1: url })} onUploadingChange={setUploading1} />
      <ImageUploader label="Photo 2" testid="press-photo2" value={form.photo2} onChange={(url) => setForm({ ...form, photo2: url })} onUploadingChange={setUploading2} />
      <Field data-testid="press-contact" label="Contact email" value={form.contact_email} onChange={set("contact_email")} />
      <button data-testid="press-submit" disabled={saving || uploading1 || uploading2} className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50">
        {(uploading1 || uploading2) ? "Uploading image…" : saving ? "Saving..." : "Save"}
      </button>
    </form>
  );
}

/* ---------- Quotes ---------- */

function QuotesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankQuote);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => setItems((await api.get("/quotes")).data);
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/quotes", form); setForm(blankQuote); toast.success("Quote added"); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/quotes/${id}`); load();
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/quotes/${editing.id}`, { text: editing.text, author: editing.author || "" });
      toast.success("Updated"); setEditing(null); load();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };
  const set = (k) => (e) => setEditing((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <form onSubmit={submit} className="space-y-3 p-6 border border-[var(--line)] rounded-sm" data-testid="quote-form">
        <h3 className="font-serif-display text-2xl mb-4">New quote</h3>
        <p className="text-xs text-[var(--ink-soft)] -mt-2 mb-2">One quote rotates daily through the whole library.</p>
        <TextArea data-testid="quote-text" label="Quote text" required value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} />
        <Field data-testid="quote-author" label="Author (optional)" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
        <button data-testid="quote-submit" className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)]">Add quote</button>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif-display text-2xl mb-4">Library ({items.length})</h3>
        {items.map((q) => (
          <ItemRow key={q.id}
            onEdit={() => setEditing({ ...q })}
            onDelete={() => del(q.id)}
            editTestId={`quote-edit-${q.id}`}
            deleteTestId={`quote-delete-${q.id}`}
          >
            <div className="text-sm italic">&ldquo;{q.text}&rdquo;</div>
            {q.author && <div className="text-xs text-[var(--ink-soft)] mt-1">— {q.author}</div>}
          </ItemRow>
        ))}
      </div>

      {editing && (
        <EditModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Edit quote" onSave={save} saving={saving}>
          <TextArea data-testid="quote-edit-text" label="Quote text" value={editing.text || ""} onChange={set("text")} rows={4} />
          <Field data-testid="quote-edit-author" label="Author" value={editing.author || ""} onChange={set("author")} />
        </EditModal>
      )}
    </div>
  );
}

/* ---------- Journal ---------- */

function JournalAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankPost);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  const load = async () => setItems((await api.get("/posts")).data);
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (uploading) { toast.error("Please wait for the image upload to finish."); return; }
    try { await api.post("/posts", form); setForm(blankPost); toast.success("Entry published"); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/posts/${id}`); load();
  };
  const save = async () => {
    if (editUploading) { toast.error("Please wait for the image upload to finish."); return; }
    setSaving(true);
    try {
      await api.put(`/posts/${editing.id}`, {
        title: editing.title, body: editing.body,
        excerpt: editing.excerpt || "", image_url: editing.image_url || "",
      });
      toast.success("Updated"); setEditing(null); load();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };
  const set = (k) => (e) => setEditing((prev) => ({ ...prev, [k]: e.target.value }));

  // Full post body must be fetched by slug when editing (list endpoint omits body)
  const openEdit = async (p) => {
    try {
      const { data } = await api.get(`/posts/${p.slug}`);
      setEditing({ ...p, body: data.body || "", excerpt: data.excerpt || p.excerpt || "", image_url: data.image_url || p.image_url || "" });
    } catch {
      setEditing({ ...p, body: "" });
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <form onSubmit={submit} className="space-y-3 p-6 border border-[var(--line)] rounded-sm">
        <h3 className="font-serif-display text-2xl mb-4">New journal entry</h3>
        <Field data-testid="post-title" label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <TextArea data-testid="post-excerpt" label="Excerpt (short summary, shown in list)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} />
        <ImageUploader label="Header image (optional)" testid="post-image" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} onUploadingChange={setUploading} />
        <TextArea data-testid="post-body" label="Body (paragraphs separated by blank lines)" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={10} />
        <button data-testid="post-submit" disabled={uploading} className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50">
          {uploading ? "Uploading image…" : "Publish"}
        </button>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif-display text-2xl mb-4">Published ({items.length})</h3>
        {items.map((p) => (
          <ItemRow key={p.id}
            onEdit={() => openEdit(p)}
            onDelete={() => del(p.id)}
            editTestId={`post-edit-${p.id}`}
            deleteTestId={`post-delete-${p.id}`}
          >
            <div className="font-medium truncate">{p.title}</div>
            <div className="text-xs text-[var(--ink-soft)] truncate">{p.excerpt || "—"}</div>
            <div className="text-[10px] text-[var(--ink-soft)] font-mono-acc mt-1">/journal/{p.slug}</div>
          </ItemRow>
        ))}
      </div>

      {editing && (
        <EditModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Edit journal entry" onSave={save} saving={saving || editUploading}>
          <Field data-testid="post-edit-title" label="Title" value={editing.title || ""} onChange={set("title")} />
          <TextArea data-testid="post-edit-excerpt" label="Excerpt" value={editing.excerpt || ""} onChange={set("excerpt")} rows={2} />
          <ImageUploader label="Header image" testid="post-edit-image" value={editing.image_url || ""} onChange={(url) => setEditing((prev) => ({ ...prev, image_url: url }))} onUploadingChange={setEditUploading} />
          <TextArea data-testid="post-edit-body" label="Body" value={editing.body || ""} onChange={set("body")} rows={10} />
        </EditModal>
      )}
    </div>
  );
}

/* ---------- Notifications ---------- */

function NotificationsAdmin() {
  const [settings, setSettings] = useState({ mode: "realtime", hour_utc: 13, pending_count: 0 });
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const { data } = await api.get("/notifications/settings");
    setSettings(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.put("/notifications/settings", { mode: next.mode, hour_utc: next.hour_utc });
      toast.success("Notification settings saved");
      load();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const sendNow = async () => {
    try {
      const { data } = await api.post("/notifications/send-digest-now");
      toast.success(data.sent_items > 0 ? `Digest sent with ${data.sent_items} item${data.sent_items === 1 ? "" : "s"}` : "Nothing in the queue right now");
      load();
    } catch { toast.error("Could not send digest"); }
  };

  const tzLabel = (h) => {
    const fmt = (offset) => {
      const local = (h + 24 + offset) % 24;
      const period = local >= 12 ? "PM" : "AM";
      const d = local % 12 === 0 ? 12 : local % 12;
      return `${d} ${period}`;
    };
    return `≈ ${fmt(-5)} ET · ${fmt(-6)} CT · ${fmt(-7)} MT · ${fmt(-8)} PT`;
  };

  return (
    <div className="max-w-2xl space-y-6 p-6 border border-[var(--line)] rounded-sm">
      <div>
        <h3 className="font-serif-display text-2xl mb-1">Email notifications</h3>
        <p className="text-sm text-[var(--ink-soft)]">Sent to <strong>50statessober@gmail.com</strong></p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button data-testid="notif-mode-realtime"
          onClick={() => save({ ...settings, mode: "realtime" })}
          disabled={saving}
          className={`p-5 border rounded-sm text-left transition ${settings.mode === "realtime" ? "border-[var(--terracotta)] bg-[var(--terracotta)]/5" : "border-[var(--line)] hover:border-[var(--ink-soft)]"}`}>
          <div className="font-mono-acc text-[10px] tracking-widest text-[var(--forest)] mb-1">REAL-TIME</div>
          <div className="font-serif-display text-xl mb-1">As it happens</div>
          <div className="text-xs text-[var(--ink-soft)]">One email per event, the moment it happens.</div>
        </button>
        <button data-testid="notif-mode-digest"
          onClick={() => save({ ...settings, mode: "digest" })}
          disabled={saving}
          className={`p-5 border rounded-sm text-left transition ${settings.mode === "digest" ? "border-[var(--terracotta)] bg-[var(--terracotta)]/5" : "border-[var(--line)] hover:border-[var(--ink-soft)]"}`}>
          <div className="font-mono-acc text-[10px] tracking-widest text-[var(--forest)] mb-1">DAILY DIGEST</div>
          <div className="font-serif-display text-xl mb-1">One morning email</div>
          <div className="text-xs text-[var(--ink-soft)]">Single summary email at your chosen hour.</div>
        </button>
      </div>

      {settings.mode === "digest" && (
        <div className="space-y-3">
          <label className="block">
            <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">Delivery hour (UTC)</div>
            <select data-testid="notif-hour"
              value={settings.hour_utc}
              onChange={(e) => save({ ...settings, hour_utc: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2.5 border border-[var(--line)] rounded-sm text-sm bg-[var(--bg)]">
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{i.toString().padStart(2, "0")}:00 UTC — {tzLabel(i)}</option>
              ))}
            </select>
          </label>

          <div className="flex items-center justify-between p-4 bg-[var(--bg-alt)] rounded-sm">
            <div className="text-sm">
              <strong className="text-[var(--forest)]">{settings.pending_count}</strong>
              {" "}{settings.pending_count === 1 ? "item" : "items"} queued for the next digest
            </div>
            <button data-testid="notif-send-now" onClick={sendNow}
              className="text-xs bg-[var(--ink)] text-white px-4 py-2 rounded-full hover:bg-[var(--forest)] transition">
              Send digest now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Donation ---------- */

function DonationAdmin() {
  const [form, setForm] = useState({ qr_data: "", label: "", description: "" });
  useEffect(() => { api.get("/donation").then((r) => setForm({ qr_data: r.data.qr_data || "", label: r.data.label || "", description: r.data.description || "" })); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.put("/donation", form); toast.success("Donation settings updated"); }
    catch { toast.error("Failed"); }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-3 p-6 border border-[var(--line)] rounded-sm">
      <h3 className="font-serif-display text-2xl mb-4">Donation page</h3>
      <Field data-testid="don-qr" label="QR data (URL or text encoded in QR)" required value={form.qr_data} onChange={(e) => setForm({ ...form, qr_data: e.target.value })} />
      <Field data-testid="don-label" label="Label (overline text)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
      <TextArea data-testid="don-desc" label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      <button data-testid="don-submit" className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)]">Save</button>
    </form>
  );
}

/* ---------- Social links ---------- */

function SocialAdmin() {
  const [form, setForm] = useState({ facebook: "", instagram: "", tiktok: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/social").then((r) => setForm({
      facebook: r.data.facebook || "",
      instagram: r.data.instagram || "",
      tiktok: r.data.tiktok || "",
    }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put("/social", form); toast.success("Social links saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-4 p-6 border border-[var(--line)] rounded-sm">
      <div>
        <h3 className="font-serif-display text-2xl mb-1">Follow us</h3>
        <p className="text-xs text-[var(--ink-soft)]">Paste the full profile URL. Icons only appear on the site once a URL is set — leave blank to hide any platform.</p>
      </div>
      <Field data-testid="social-facebook" label="Facebook URL" value={form.facebook} onChange={set("facebook")} placeholder="https://www.facebook.com/YourPage" />
      <Field data-testid="social-instagram" label="Instagram URL" value={form.instagram} onChange={set("instagram")} placeholder="https://www.instagram.com/yourhandle" />
      <Field data-testid="social-tiktok" label="TikTok URL" value={form.tiktok} onChange={set("tiktok")} placeholder="https://www.tiktok.com/@yourhandle" />
      <button data-testid="social-submit" disabled={saving} className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50">
        {saving ? "Saving…" : "Save"}
      </button>
    </form>
  );
}

/* ---------- Photo Album (up to 4 albums, photos + videos) ---------- */

const MAX_ALBUMS = 4;
const blankAlbumEvent = { name: "", event_date: "", description: "", cover_url: "" };
const blankAlbumPhoto = { url: "", caption: "", photo_date: "", media_type: "photo", thumb_url: "" };

function AlbumAdmin() {
  const [events, setEvents] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState(blankAlbumEvent);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editUploading, setEditUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const loadEvents = useCallback(async () => {
    setEvents((await api.get("/album/events")).data);
  }, []);
  const loadPhotos = useCallback(async () => {
    setPhotos((await api.get("/album/photos")).data);
  }, []);
  useEffect(() => { loadEvents(); loadPhotos(); }, [loadEvents, loadPhotos]);

  const atMax = events.length >= MAX_ALBUMS;

  const submit = async (e) => {
    e.preventDefault();
    if (atMax) { toast.error(`You already have ${MAX_ALBUMS} albums. Delete one first.`); return; }
    if (uploading) { toast.error("Please wait for the image upload to finish."); return; }
    if (!form.name || !form.event_date) { toast.error("Name & date are required."); return; }
    try {
      await api.post("/album/events", form);
      setForm(blankAlbumEvent);
      toast.success("Album created");
      loadEvents();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to create album";
      toast.error(msg);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this album and all its photos & videos?")) return;
    await api.delete(`/album/events/${id}`);
    toast.success("Deleted");
    loadEvents(); loadPhotos();
  };

  const save = async () => {
    if (editUploading) { toast.error("Please wait for the image upload to finish."); return; }
    setSaving(true);
    try {
      await api.put(`/album/events/${editing.id}`, {
        name: editing.name, event_date: editing.event_date,
        description: editing.description || "", cover_url: editing.cover_url || "",
      });
      toast.success("Updated"); setEditing(null); loadEvents();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };

  const setE = (k) => (e) => setEditing((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="space-y-10">
      <div className="grid md:grid-cols-2 gap-10">
        <form onSubmit={submit} className="space-y-3 p-6 border border-[var(--line)] rounded-sm" data-testid="album-event-form">
          <h3 className="font-serif-display text-2xl mb-2">
            {atMax ? "You're at the limit" : "New album"}
          </h3>
          <p className="text-xs text-[var(--ink-soft)] mb-4">
            You can have up to <strong>{MAX_ALBUMS} albums</strong>. Rename any time. Each album can hold photos <em>and</em> videos.
          </p>
          <fieldset disabled={atMax} className="space-y-3 disabled:opacity-50 disabled:pointer-events-none">
            <Field data-testid="album-event-name" label="Album name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Field data-testid="album-event-date" label="Album date" type="date" required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
            <TextArea data-testid="album-event-description" label="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <ImageUploader label="Cover photo (optional)" testid="album-event-cover" value={form.cover_url} onChange={(url) => setForm({ ...form, cover_url: url })} onUploadingChange={setUploading} />
            <button data-testid="album-event-submit" className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50" disabled={atMax}>
              Create album
            </button>
          </fieldset>
          {atMax && (
            <p className="text-xs text-[var(--terracotta-dark)] font-medium">Delete an existing album to make room for a new one.</p>
          )}
        </form>

        <div className="space-y-3">
          <h3 className="font-serif-display text-2xl mb-4">Your albums ({events.length}/{MAX_ALBUMS})</h3>
          {events.length === 0 && <p className="text-sm text-[var(--ink-soft)]">No albums yet. Create your first on the left.</p>}
          {events.map((ev) => (
            <div key={ev.id} className="border border-[var(--line)] rounded-sm">
              <ItemRow
                onEdit={() => setEditing({ ...ev })}
                onDelete={() => del(ev.id)}
                editTestId={`album-event-edit-${ev.id}`}
                deleteTestId={`album-event-delete-${ev.id}`}
              >
                <button
                  type="button"
                  className="text-left w-full"
                  onClick={() => setExpandedId(expandedId === ev.id ? null : ev.id)}
                  data-testid={`album-event-toggle-${ev.id}`}
                >
                  <div className="font-medium">{ev.name}</div>
                  <div className="text-xs text-[var(--ink-soft)]">
                    {ev.event_date} · {ev.photo_count} {ev.photo_count === 1 ? "item" : "items"}
                  </div>
                </button>
              </ItemRow>
              {expandedId === ev.id && (
                <div className="border-t border-[var(--line)] p-4 bg-[var(--bg-alt)]">
                  <AlbumPhotoManager
                    eventId={ev.id}
                    eventDate={ev.event_date}
                    photos={photos.filter((p) => p.event_id === ev.id)}
                    reload={() => { loadPhotos(); loadEvents(); }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <EditModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Rename album" onSave={save} saving={saving || editUploading}>
          <Field data-testid="album-event-edit-name" label="Album name" value={editing.name || ""} onChange={setE("name")} />
          <Field data-testid="album-event-edit-date" label="Album date" type="date" value={editing.event_date || ""} onChange={setE("event_date")} />
          <TextArea data-testid="album-event-edit-description" label="Description" value={editing.description || ""} onChange={setE("description")} />
          <ImageUploader label="Cover photo" testid="album-event-edit-cover" value={editing.cover_url || ""} onChange={(url) => setEditing((prev) => ({ ...prev, cover_url: url }))} onUploadingChange={setEditUploading} />
        </EditModal>
      )}
    </div>
  );
}

function AlbumCommentsModeration() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyOpen, setReplyOpen] = useState(null); // comment id being replied to
  const [replyText, setReplyText] = useState("");
  const [replySaving, setReplySaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setComments((await api.get("/admin/album/comments")).data); }
    catch { setComments([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (cid) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    try { await api.delete(`/admin/album/comments/${cid}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const openReply = (c) => {
    setReplyOpen(c.id);
    setReplyText(c.admin_reply || "");
  };

  const saveReply = async (cid) => {
    const text = replyText.trim();
    if (!text) { toast.error("Reply cannot be empty."); return; }
    setReplySaving(true);
    try {
      const { data } = await api.post(`/admin/album/comments/${cid}/reply`, { reply: text });
      setComments((prev) => prev.map((c) => (c.id === cid ? data : c)));
      toast.success("Reply posted");
      setReplyOpen(null);
      setReplyText("");
    } catch {
      toast.error("Could not save reply");
    } finally {
      setReplySaving(false);
    }
  };

  const removeReply = async (cid) => {
    if (!window.confirm("Remove your reply?")) return;
    try {
      const { data } = await api.delete(`/admin/album/comments/${cid}/reply`);
      setComments((prev) => prev.map((c) => (c.id === cid ? data : c)));
      toast.success("Reply removed");
    } catch { toast.error("Remove failed"); }
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const resolve = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

  return (
    <div className="border-t border-[var(--line)] pt-8" data-testid="album-comments-moderation">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline mb-1">Moderation</div>
          <h3 className="font-serif-display text-2xl">Notes visitors left</h3>
          <p className="text-xs text-[var(--ink-soft)] mt-1">Reply as Rick &amp; Stephanie or delete any note that feels wrong.</p>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-xs px-3 py-1.5 border border-[var(--line)] rounded-full hover:bg-[var(--bg-alt)]"
          data-testid="album-comments-refresh"
        >
          Refresh
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-[var(--ink-soft)]">Loading…</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-[var(--ink-soft)]">No notes yet. When visitors leave a message under a photo, it will show up here.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} data-testid={`admin-comment-${c.id}`}
              className="flex items-start gap-3 p-3 border border-[var(--line)] rounded-sm">
              {c.photo_url && (
                <img src={resolve(c.photo_url)} alt="" className="w-14 h-14 object-cover rounded-sm shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[var(--ink-soft)] font-mono-acc mb-0.5">
                  <span className="text-[var(--forest)] font-medium">{c.name}</span>
                  {c.event_name && <span className="ml-2">on {c.event_name}</span>}
                  <span className="ml-2">· {(c.created_at || "").slice(0, 10)}</span>
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-line">{c.message}</p>

                {c.admin_reply && replyOpen !== c.id && (
                  <div
                    className="mt-2 pl-3 py-2 pr-3 border-l-2 rounded-r-sm text-sm bg-[var(--bg-alt)]"
                    style={{ borderColor: "var(--terracotta)" }}
                    data-testid={`admin-reply-preview-${c.id}`}
                  >
                    <div className="text-[10px] font-mono-acc tracking-wider uppercase text-[var(--forest)] mb-0.5">
                      Rick &amp; Stephanie replied · {(c.admin_reply_at || "").slice(0, 10)}
                    </div>
                    <p className="italic leading-relaxed whitespace-pre-line">{c.admin_reply}</p>
                    <div className="flex gap-2 mt-2">
                      <button type="button" onClick={() => openReply(c)}
                        className="text-[11px] px-2.5 py-1 border border-[var(--line)] rounded-full hover:bg-[var(--bg)]"
                        data-testid={`admin-reply-edit-${c.id}`}>
                        Edit reply
                      </button>
                      <button type="button" onClick={() => removeReply(c.id)}
                        className="text-[11px] px-2.5 py-1 border border-red-300 text-red-600 rounded-full hover:bg-red-50"
                        data-testid={`admin-reply-remove-${c.id}`}>
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                {replyOpen === c.id && (
                  <div className="mt-2 space-y-2" data-testid={`admin-reply-form-${c.id}`}>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={2}
                      maxLength={500}
                      placeholder="Write a reply as Rick & Stephanie…"
                      className="w-full px-3 py-2 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]"
                      data-testid={`admin-reply-input-${c.id}`}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[var(--ink-soft)] font-mono-acc tracking-wider">
                        {replyText.length}/500
                      </span>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => { setReplyOpen(null); setReplyText(""); }}
                          className="text-xs px-3 py-1.5 border border-[var(--line)] rounded-full hover:bg-[var(--bg-alt)]">
                          Cancel
                        </button>
                        <button type="button" onClick={() => saveReply(c.id)} disabled={replySaving}
                          className="text-xs px-3 py-1.5 bg-[var(--terracotta)] text-[var(--ink)] rounded-full hover:bg-[var(--terracotta-dark)] disabled:opacity-50"
                          data-testid={`admin-reply-save-${c.id}`}>
                          {replySaving ? "Saving…" : (c.admin_reply ? "Update reply" : "Send reply")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {!c.admin_reply && replyOpen !== c.id && (
                  <button
                    type="button"
                    onClick={() => openReply(c)}
                    className="mt-2 text-xs px-3 py-1 border border-[var(--terracotta)] text-[var(--forest)] rounded-full hover:bg-[var(--terracotta)] hover:text-white transition-colors"
                    data-testid={`admin-reply-open-${c.id}`}
                  >
                    ♥ Reply as Rick &amp; Stephanie
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => del(c.id)}
                className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-sm shrink-0"
                title="Delete"
                data-testid={`admin-comment-delete-${c.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AlbumPhotoManager({ eventId, eventDate, photos, reload }) {
  const [pForm, setPForm] = useState({ ...blankAlbumPhoto, photo_date: eventDate || "" });
  const [pUploading, setPUploading] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [editPhotoUploading, setEditPhotoUploading] = useState(false);

  const isVideo = pForm.media_type === "video";
  const isYouTubeUrl = /(?:youtu\.be\/|youtube\.com\/|vimeo\.com\/)/.test(pForm.url);

  const addPhoto = async (e) => {
    e.preventDefault();
    if (pUploading) { toast.error("Please wait for the upload to finish."); return; }
    if (!pForm.url) {
      toast.error(isVideo ? "Paste a YouTube/Vimeo URL or upload a video first." : "Please upload a photo first.");
      return;
    }
    try {
      await api.post("/album/photos", { ...pForm, event_id: eventId });
      setPForm({ ...blankAlbumPhoto, photo_date: eventDate || "" });
      toast.success(isVideo ? "Video added" : "Photo added");
      reload();
    } catch { toast.error("Failed to add"); }
  };

  const delPhoto = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/album/photos/${id}`);
    toast.success("Deleted");
    reload();
  };

  const savePhoto = async () => {
    if (editPhotoUploading) { toast.error("Please wait for the upload to finish."); return; }
    setSavingPhoto(true);
    try {
      await api.put(`/album/photos/${editingPhoto.id}`, {
        event_id: editingPhoto.event_id,
        url: editingPhoto.url,
        caption: editingPhoto.caption || "",
        photo_date: editingPhoto.photo_date || null,
        media_type: editingPhoto.media_type || "photo",
        thumb_url: editingPhoto.thumb_url || "",
      });
      toast.success("Updated"); setEditingPhoto(null); reload();
    } catch { toast.error("Update failed"); }
    setSavingPhoto(false);
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= photos.length) return;
    const ids = photos.map((p) => p.id);
    const [moved] = ids.splice(idx, 1);
    ids.splice(target, 0, moved);
    try {
      await api.post("/album/photos/reorder", { event_id: eventId, ordered_ids: ids });
      reload();
    } catch { toast.error("Reorder failed"); }
  };

  const setE = (k) => (e) => setEditingPhoto((prev) => ({ ...prev, [k]: e.target.value }));
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const resolve = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

  return (
    <div className="space-y-5">
      {/* Type toggle */}
      <div className="inline-flex rounded-full border border-[var(--line)] p-1 bg-[var(--bg)]" role="tablist">
        <button
          type="button"
          onClick={() => setPForm({ ...pForm, media_type: "photo", url: "", thumb_url: "" })}
          data-testid={`album-mediatype-photo-${eventId}`}
          className={`px-4 py-1.5 text-xs rounded-full transition-colors ${
            !isVideo ? "bg-[var(--ink)] text-[var(--bg)]" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"
          }`}
        >
          Photo
        </button>
        <button
          type="button"
          onClick={() => setPForm({ ...pForm, media_type: "video", url: "", thumb_url: "" })}
          data-testid={`album-mediatype-video-${eventId}`}
          className={`px-4 py-1.5 text-xs rounded-full transition-colors ${
            isVideo ? "bg-[var(--ink)] text-[var(--bg)]" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"
          }`}
        >
          Video
        </button>
      </div>

      <form onSubmit={addPhoto} className="grid md:grid-cols-2 gap-3 items-start" data-testid={`album-photo-form-${eventId}`}>
        <div className="space-y-3">
          {isVideo ? (
            <>
              <label className="block">
                <span className="text-xs text-[var(--ink-soft)] font-mono-acc tracking-wider block mb-1.5">
                  YouTube or Vimeo URL
                </span>
                <input
                  type="url"
                  value={pForm.url}
                  onChange={(e) => setPForm({ ...pForm, url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=…"
                  data-testid={`album-video-url-${eventId}`}
                  className="w-full px-3 py-2 border border-[var(--line)] rounded-sm text-sm bg-[var(--bg)] focus:outline-none focus:border-[var(--forest)]"
                />
              </label>
              <p className="text-[11px] text-[var(--ink-soft)]">
                Or upload a video file:
              </p>
              <ImageUploader
                label=""
                testid={`album-video-upload-${eventId}`}
                value={isYouTubeUrl ? "" : pForm.url}
                onChange={(url) => setPForm({ ...pForm, url })}
                onUploadingChange={setPUploading}
                accept="video/*"
              />
            </>
          ) : (
            <ImageUploader
              label="Upload a photo"
              testid={`album-photo-upload-${eventId}`}
              value={pForm.url}
              onChange={(url) => setPForm({ ...pForm, url })}
              onUploadingChange={setPUploading}
            />
          )}
        </div>
        <div className="space-y-3">
          <Field data-testid={`album-photo-date-${eventId}`} label="Date (defaults to album date)" type="date" value={pForm.photo_date || ""} onChange={(e) => setPForm({ ...pForm, photo_date: e.target.value })} />
          <TextArea data-testid={`album-photo-caption-${eventId}`} label="Caption (optional)" value={pForm.caption} onChange={(e) => setPForm({ ...pForm, caption: e.target.value })} rows={2} />
          <button data-testid={`album-photo-submit-${eventId}`} className="w-full bg-[var(--forest)] text-white py-2.5 rounded-sm text-sm hover:opacity-90 disabled:opacity-50" disabled={pUploading || !pForm.url}>
            {isVideo ? "Add video to album" : "Add photo to album"}
          </button>
        </div>
      </form>

      {photos.length > 0 && (
        <div>
          <div className="text-xs text-[var(--ink-soft)] mb-3 font-mono-acc tracking-wider">
            {photos.length} ITEM{photos.length === 1 ? "" : "S"} — USE ARROWS TO REORDER
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.map((p, i) => {
              const isVid = p.media_type === "video";
              const thumb = isVid ? (p.thumb_url ? resolve(p.thumb_url) : "") : resolve(p.url);
              return (
                <div key={p.id} className="border border-[var(--line)] rounded-sm overflow-hidden bg-[var(--bg)]" data-testid={`album-photo-item-${p.id}`}>
                  <div className="relative w-full aspect-square bg-[var(--bg-alt)]">
                    {thumb ? (
                      <img src={thumb} alt={p.caption || "album"} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[var(--ink-soft)] text-xs px-2 text-center">
                        {isVid ? "Video" : "No image"}
                      </div>
                    )}
                    {isVid && (
                      <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-black/70 text-white text-[10px] rounded font-mono-acc tracking-wider">
                        VIDEO
                      </div>
                    )}
                  </div>
                  <div className="p-2 text-xs">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-[var(--ink-soft)] truncate flex-1">{p.caption || p.photo_date || "—"}</div>
                      <div className="inline-flex items-center gap-1.5 text-[var(--ink-soft)] shrink-0" data-testid={`album-photo-reactions-${p.id}`}>
                        <span className="inline-flex items-center gap-0.5">
                          <span className="text-[var(--terracotta)]" aria-hidden="true">♥</span>{p.likes || 0}
                        </span>
                        <span className="inline-flex items-center gap-0.5">
                          <span aria-hidden="true">👍</span>{p.thumbs_up || 0}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex gap-1">
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="px-1.5 py-0.5 border border-[var(--line)] rounded text-[10px] disabled:opacity-30" data-testid={`album-photo-up-${p.id}`}>↑</button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === photos.length - 1} className="px-1.5 py-0.5 border border-[var(--line)] rounded text-[10px] disabled:opacity-30" data-testid={`album-photo-down-${p.id}`}>↓</button>
                      </div>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => setEditingPhoto({ ...p })} className="p-1 text-[var(--ink-soft)] hover:text-[var(--forest)]" data-testid={`album-photo-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button type="button" onClick={() => delPhoto(p.id)} className="p-1 text-red-600 hover:text-red-800" data-testid={`album-photo-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {editingPhoto && (
        <EditModal open={!!editingPhoto} onOpenChange={(o) => !o && setEditingPhoto(null)} title={editingPhoto.media_type === "video" ? "Edit video" : "Edit photo"} onSave={savePhoto} saving={savingPhoto || editPhotoUploading}>
          {editingPhoto.media_type === "video" ? (
            <>
              <label className="block">
                <span className="text-xs text-[var(--ink-soft)] font-mono-acc tracking-wider block mb-1.5">Video URL</span>
                <input type="url" value={editingPhoto.url || ""} onChange={setE("url")}
                  className="w-full px-3 py-2 border border-[var(--line)] rounded-sm text-sm bg-[var(--bg)] focus:outline-none focus:border-[var(--forest)]"
                  data-testid="album-photo-edit-video-url" />
              </label>
              <ImageUploader label="Custom thumbnail (optional)" testid="album-photo-edit-thumb" value={editingPhoto.thumb_url || ""} onChange={(url) => setEditingPhoto((prev) => ({ ...prev, thumb_url: url }))} onUploadingChange={setEditPhotoUploading} />
            </>
          ) : (
            <ImageUploader label="Photo" testid="album-photo-edit-upload" value={editingPhoto.url || ""} onChange={(url) => setEditingPhoto((prev) => ({ ...prev, url }))} onUploadingChange={setEditPhotoUploading} />
          )}
          <Field data-testid="album-photo-edit-date" label="Date" type="date" value={editingPhoto.photo_date || ""} onChange={setE("photo_date")} />
          <TextArea data-testid="album-photo-edit-caption" label="Caption" value={editingPhoto.caption || ""} onChange={setE("caption")} rows={2} />
        </EditModal>
      )}
    </div>
  );
}

